import { FormEvent, useState } from 'react';
import { api, ApiError, Profile } from '../api';
import { forgetPinLogin, pinLoginOf, pinNameOf, rememberPinLogin } from '../lib/pin';
import { greeting } from '../lib/greeting';
import { LoginBackdrop } from '../components/LoginBackdrop';
import { Logo } from '../components/Logo';
import { MissingNote } from '../components/MissingNote';
import { PinPad } from './PinPad';

/*
 * Вход. Если на устройстве настроен пин-код — сразу пин-пад, логин с паролем
 * остаются запасным входом (пин забыт, чужое устройство, сброс после неудач).
 *
 * Логотип и подпись — над карточкой, посередине страницы. В карточке —
 * приветствие по времени суток; если устройство помнит человека (пин),
 * то по имени и фамилии. Никнейм в интерфейсе не показываем.
 */

const fullName = (u: { firstName?: string | null; lastName?: string | null }) =>
  `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();

export function Login({
  onLogin,
  notice,
}: {
  onLogin: (profile: Profile) => void;
  /** Причина возврата на экран входа, например автовыход по бездействию. */
  notice?: string;
}) {
  const savedLogin = pinLoginOf();
  const savedName = pinNameOf();
  const [mode, setMode] = useState<'pin' | 'password'>(savedLogin ? 'pin' : 'password');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [pinErr, setPinErr] = useState('');
  const [busy, setBusy] = useState(false);

  const hi = greeting(new Date().getHours());
  // «Илья, добрый вечер!» — сначала имя, потом приветствие; без имени — просто «Добрый вечер!»
  const firstName = savedName?.split(' ')[0];
  const title = mode === 'pin' && firstName ? `${firstName}, ${hi.toLowerCase()}!` : `${hi}!`;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const r = await api.login(login.trim(), password);
      // на этом устройстве пин принадлежит одному логину
      if (r.user.hasPin) rememberPinLogin(r.user.login, fullName(r.user));
      else if (savedLogin && savedLogin !== r.user.login) forgetPinLogin();
      onLogin(r);
    } catch (e) {
      setErr(e instanceof ApiError ? 'Неверный логин или пароль' : 'Сервер недоступен');
    } finally {
      setBusy(false);
    }
  };

  const submitPin = async (pin: string) => {
    if (!savedLogin) return;
    setPinErr('');
    setBusy(true);
    try {
      const r = await api.pinLogin(savedLogin, pin);
      // имя могло смениться или ещё не было запомнено
      rememberPinLogin(r.user.login, fullName(r.user));
      onLogin(r);
    } catch (e) {
      const message = e instanceof ApiError ? e.message : 'Сервер недоступен';
      // пин сброшен сервером — устройство про него забывает
      if (e instanceof ApiError && /паролю/.test(message)) {
        forgetPinLogin();
        setMode('password');
        setErr(message);
      } else {
        setPinErr(message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <LoginBackdrop />
      <div className="login-head">
        <Logo variant="full" className="login-logo" />
        <div className="login-kind">Корпоративная система</div>
      </div>
      <div className="login-card">
        <h1 className="login-hi">{title}</h1>
        {notice && <div className="banner info">{notice}</div>}

        {mode === 'pin' && savedLogin ? (
          <PinPad
            title="Введите пин-код"
            error={pinErr || undefined}
            busy={busy}
            onComplete={submitPin}
            onCancel={() => {
              setPinErr('');
              setLogin(savedLogin);
              setMode('password');
            }}
            cancelLabel="Войти по логину и паролю"
          />
        ) : (
          <>
            <form className="form" onSubmit={submit}>
              <div className="field">
                <label htmlFor="login-name">
                  Логин <i>*</i>
                </label>
                <input
                  id="login-name"
                  className={`input${login.trim() ? '' : ' missing'}`}
                  autoComplete="username"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                />
                {!login.trim() && <MissingNote />}
              </div>
              <div className="field">
                <label htmlFor="login-pass">
                  Пароль <i>*</i>
                </label>
                <input
                  id="login-pass"
                  className={`input${password ? '' : ' missing'}`}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {!password && <MissingNote />}
              </div>
              <button className="btn primary wide" disabled={busy || !login.trim() || !password}>
                {busy ? 'Входим…' : 'Войти'}
              </button>
              {err && <div className="banner crit">{err}</div>}
            </form>
            {savedLogin && (
              <button
                type="button"
                className="btn link"
                onClick={() => {
                  setErr('');
                  setMode('pin');
                }}
              >
                Войти по пин-коду
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
